import { Component, AfterViewInit, EventEmitter, ElementRef, forwardRef, Input, Output, ViewChild, OnDestroy } from '@angular/core';
import { ControlValueAccessor, NgControl, NG_VALUE_ACCESSOR, Validator, FormControl, NG_VALIDATORS } from '@angular/forms';

import Inputmask from 'inputmask';

export interface Style {
    fontWeight: string;
}

/** Bibliotecas:
 * https://github.com/RobinHerbots/Inputmask
 */

@Component({
    selector: 'sgt-decimal2',
    templateUrl: 'decimal2.component.html',
    styleUrls: ['./decimal2.component.less'],
    providers: [
        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => Decimal2Component), multi: true },
        { provide: NG_VALIDATORS, useExisting: forwardRef(() => Decimal2Component), multi: true }
    ]
})
export class Decimal2Component implements AfterViewInit, ControlValueAccessor, Validator, OnDestroy {
    @Input() ngModel: any;
    @Output() ngModelChange = new EventEmitter<number>();

    /**
     * Quandidade de digitos fracionários
     *
     * @default 2
     */
    @Input() digits = 2;
    /**
     * Separador de milhar
     *
     * @default '.'
     */
    @Input() groupSeparator = '.';
    /**
     * Seperador de decimal
     *
     * @default ','
     */
    @Input() radixPoint = ',';
    /**
     * Se permite digitar valores negativos
     *
     * @default false
     */
    @Input() allowMinus = false;
    /**
     * Placeholder do input
     *
     * @default ''
     */
    @Input() placeholder = '';
    /**
     * Valor mínimo permitido
     *
     * @default null (nenhum)
     */
    @Input() min: number;
    /**
     * Valor máximo permitido
     *
     * @default null (nenhum)
     */
    @Input() max: number;
    /**
     * Se o valor quando for null deve ser considerado como zero (0)
     *
     * @default false
     */
    @Input() nullIsZero = false;
    /**
     * Nome do input decimal
     */
    @Input() name: string;
    /**
     * ID do input decimal
     */
     @Input() id: string;
    /**
     * Se o valor é obrigatório
     */
    @Input() required: any;
    /**
     * Se deve desabilitar o input
     */
    @Input() disabled: any;
    /**
     * Se o input é de somente leitura
     */
    @Input() readonly: any;
    @Input() ngControl: NgControl;
    /**
     * Se deve setar o foco no input ao criar o component
     */
    @Input() autofocus = false;

    /** Se deve selecionar o valor do input ao realizar o focus */
    @Input() selectValueOnFocus = false;

    /** Style personalizado do input */
    @Input() style: Style = {
        fontWeight: 'normal'
    };

    /** Ao focar no input */
    @Output() focus = new EventEmitter<FocusEvent>();
    /** Ao perder o foco no input */
    @Output() focusout = new EventEmitter<FocusEvent>();

    /**
     * Evento emitido quando o valor for alterado
     *
     * @returns o valor alterado
     */
    @Output() change = new EventEmitter<number>();

    @ViewChild('input') input: ElementRef;
    private inputElement: any;
    private inputMask: Inputmask.Instance;

    propagateChange: any = () => {};

    constructor(private el: ElementRef) { }


    ngAfterViewInit() {
        this.inputElement = this.input.nativeElement;

        jQuery(this.inputElement)
            .on('focus', () => {
                if (this.selectValueOnFocus) {
                    jQuery(this.inputElement).trigger('select')
                }
            });

        this.createInputMask();

        if (this.autofocus) {
           this.setFocus();
        }
    }

    /** Cria e aplica a máscara de campo decimal no input utilizando a biblioteca Inputmask */
    private createInputMask() {
        this.inputMask = new Inputmask({
            alias: 'decimal',
            numericInput: true,
            unmaskAsNumber: true,
            digits: this.digits,
            groupSeparator: this.groupSeparator,
            radixPoint: this.radixPoint,
            allowMinus: this.allowMinus,
            placeholder: this.placeholder,
            showMaskOnFocus: false,
            showMaskOnHover: false,
            undoOnEscape: false
        }).mask(this.inputElement);
    }

    /** Obtem o valor do input sem a formatação do mask (unmasked) */
    private getValue(): number {
        const unmaskedvalue = this.inputMask?.unmaskedvalue();
        // Se não retornar um valor válido no unmaked do input, retornamos null
        if ((!unmaskedvalue || unmaskedvalue === '') && !this.nullIsZero) {
            return null;
        }

        return Number(unmaskedvalue);
    }

    /** Valida se o valor é maior ou igual ao valor mínimo permitido */
    private validarValorMinimo(value: number): boolean {
        if (!this.min) {
            return true;
        }

        return value >= this.min;
    }

    /** Valida se o valor é menor ou igual ao valor máximo permitido */
    private validarValorMaximo(value: number): boolean {
        if (!this.max) {
            return true;
        }

        return value <= this.max;
    }

     /** Realiza a validação do FormControl de decimal */
     validate(c: FormControl) {
        const value = this.getValue();

        // Validando valor mínimo
        const isMinValid = this.validarValorMinimo(value);
        if (!isMinValid) {
            return {
                min: {
                    valid: false
                }
            }
        }
        // Validando valor máximo
        const isMaxValid = this.validarValorMaximo(value);
        if (!isMaxValid) {
            return {
                max: {
                    valid: false
                }
            }
        }

        return null;
    }


    /** Seta o foco no input */
    setFocus() {
        jQuery(this.inputElement).trigger('focus');
    }

    /** Ao focar no input */
    onFocus(event: FocusEvent) {
        this.focus.emit(event);
    }

    /** Ao perder o foco no input */
    onFocusout(event: FocusEvent) {
        this.focusout.emit(event);
    }

    /** Ao digitar no input atualizamos o valor no ngModel */
    onKeyup(event: KeyboardEvent) {
        // Teclas que devem ser ignoradas
        const ignoresKeys: string[] = ['Tab'];
        if (ignoresKeys.includes(event.key)) {
            return;
        }

        // Propagando alteração do valor no ngModel
        const value = this.getValue();
        this.propagateChange(value);
    }


    /** Emite envendo quando o valor do input é alterado pelo usuário */
    onChange(event: Event) {
        this.change.emit(this.getValue());
        // Parando a propagação do evento change do input
        event?.stopPropagation();
    }

    /** Quando recebe um valor */
    writeValue(value: number) {
        if (!this.inputElement) {
            return;
        }
        // Setando o valor no inputmask
        this.inputElement.value = (value !== undefined && value !== null) ? value : '';
    }

    /** Propaga as alterações realizadas no input */
    registerOnChange(fn) {
        this.propagateChange = fn;
    }

    registerOnTouched() { }

    ngOnDestroy() {
        this.inputMask?.remove();
    }
}
